import { useEffect, useState } from "react";
import { Trash2, FileDown, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import Pager from "@/components/site/Pager";
import { api, API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 25;

export default function AdminSubscribers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/job-alerts", { params: { page, page_size: PAGE_SIZE } })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  const remove = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    await api.delete(`/admin/job-alerts/${id}`);
    toast.success("Removed");
    load();
  };

  const exportCsv = async () => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/exports/job-alerts`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `job-alerts-${new Date().toISOString().slice(0,10)}.csv`; link.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  const copyEmails = async () => {
    const emails = items.map((x) => x.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`${items.length} email${items.length === 1 ? "" : "s"} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-subscribers">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Job Alert Subscribers</h1>
          <p className="text-sm text-slate-500">
            {total} subscriber{total === 1 ? "" : "s"} · captured via the footer "Job Alerts" form.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={copyEmails} disabled={items.length === 0} data-testid="subscribers-copy-emails">
            <Copy className="mr-1 h-4 w-4" />Copy emails
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={items.length === 0} data-testid="subscribers-export-csv">
            <FileDown className="mr-1 h-4 w-4" />Export CSV
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Keywords</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Subscribed</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{s.email}</td>
                <td className="px-4 py-3 text-slate-700">{s.keywords || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-slate-700">{s.location || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)} data-testid={`subscriber-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                  </Button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-slate-400">
                  No subscribers yet. Whenever someone submits the "Job Alerts" form in the site footer, they'll appear here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} testIdPrefix="admin-subscribers-pager" />
    </div>
  );
}
